import { useTranslation } from 'react-i18next';

/**
 * "Vai al contenuto", con il contesto che salterebbe.
 *
 * È un pulsante e non un `<a href="#main">` perché un collegamento a un
 * frammento **aggiunge una voce alla cronologia** — vietato dentro
 * l'applicazione (sezione 7ter) — e per di più sovrascriveva la sezione
 * corrente nell'indirizzo: al ricaricamento si tornava sulla console.
 *
 * **Dove vive l'identità dell'ente.** Era la domanda lasciata aperta alla fine
 * di AP, ed è quella che questo file risponde. L'identità vive
 * nell'intestazione: il nome dell'ente sta nell'`<h1>` della barra, e in
 * assistenza quell'`<h1>` lo dice. Ma questo collegamento esiste **apposta per
 * saltare l'intestazione**, quindi chi lo usa arrivava su `main` senza sapere
 * in quale ente stava lavorando, né che ci stava entrando per assistenza
 * (sezione 9bis).
 *
 * La decisione è che **il collegamento se la porta dietro**. Chi salta il
 * contesto lo sente nel momento esatto in cui decide di saltarlo, che è
 * l'unico momento in cui gli serve.
 *
 * Il testo visibile resta corto e il contesto è ritagliato, non nascosto: il
 * nome accessibile **contiene** l'etichetta visibile, come WCAG 2.5.3 chiede,
 * e chi guarda lo schermo il nome dell'ente ce l'ha già davanti in cima.
 */
export function SkipLink({
  tenantName,
  impersonating,
}: {
  tenantName?: string;
  impersonating?: boolean;
}) {
  const { t } = useTranslation();

  /**
   * Prima dell'accesso non c'è nessun ente, e non c'è niente da aggiungere:
   * un contesto vuoto sarebbe una parola in più che non dice niente.
   */
  const context = tenantName
    ? t(impersonating ? 'app.skip_context_impersonated' : 'app.skip_context_tenant', {
        name: tenantName,
      })
    : '';

  return (
    <button
      type="button"
      className="skip-link"
      onClick={() => document.getElementById('main')?.focus()}
    >
      {t('app.skip_to_content')}
      {context ? <span className="visually-hidden"> {context}</span> : null}
    </button>
  );
}
